/**
 * Approval Detail Page - Routable page for viewing approval details
 * Shows stage visualization (1/3 left) and activity log (2/3 right)
 */
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled, { css, useTheme } from "styled-components";
import axios from "axios";
import useSWR from "swr";
import type { AxiosError } from "axios";
import { ApprovalStageVisualization } from "./ApprovalStageVisualization";
import { ApprovalActivityLog } from "./ApprovalActivityLog";
import {
  ApproveConfirmationModal,
  RejectConfirmationModal,
} from "./ConfirmationModals";
import { useApprovalDetail } from "./hooks/useApprovalDetail";
import { useApprovalMutations } from "./hooks/useApprovalMutations";
import { DelayedSpinner } from "../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../components/Error";
import { IDMobile } from "../../../../components/IDs/IDs";
import {
  TextXlSemibold,
  TextSmRegular,
  TextSmMedium,
} from "../../../../components/Typography/TypographyV2";
import { useRoutePath } from "../../../../util/Routing";
import { useAuthContext } from "../../../../components/Auth";
import {
  createApproverColumn,
  getApproverPersonColumnTitle,
  getApproverSectionKey,
  findActiveApprovalStage,
  canEditApprovalAssignee,
  approvalAssigneeDisabledTooltip,
  toAssigneeOptions,
} from "./utils";
import { CaretLeft, EditIcon } from "../../../../components/Icons/Icons";
import { Approvers, ApproverRow } from "./Approvers";
import type { ApprovalStage } from "../../../../types/types.Workflow";
import {
  makeUrlWithParams,
  toTitleCase,
  useMediaQueries,
  useStoreState,
} from "../../../../util/util";
import type { PIMProduct } from "../../../../types/types.PIM";
import { endpoints } from "../../../../endpoints";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { Breadcrumb } from "../../../../components/ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../../../../components/LinkUnstyled/LinkUnstyled";
import { LinkButtonLargeV2 } from "../../../../components/Buttons/Buttons";
import { AssigneePicker } from "../../../../components/AssigneePopOver";
import type { AssigneeOption } from "../../../../components/AssigneePopOver";
import { APPROVALS_LIST_RESTORE_FLAG } from "./hooks/useApprovalsListControls";
import {
  formatApproverAttributeValue,
  getApproverExtraFields,
  STAGE_APPROVER_IDENTITY_FIELDS,
} from "../RequestApproval/requestApproval.utils";

const PageRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 640px) {
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  min-height: 0;
  overflow: hidden;

  @media (max-width: 640px) {
    flex-direction: column;
    flex: none;
    min-height: auto;
    overflow: visible;
  }
`;

const StageSection = styled.div`
  position: relative;
  z-index: 1;
  width: ${({ theme }) => theme.resolve("width_xxs")};
  min-width: ${({ theme }) => theme.resolve("width_xxxs")};
  max-width: ${({ theme }) => theme.resolve("width_xxs")};
  flex-shrink: 0;
  min-height: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};

  @media (max-width: 640px) {
    width: 100%;
    min-width: unset;
    max-width: none;
    z-index: auto;
  }
`;

const StageSectionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  flex-shrink: 0;
`;

const BackLink = styled.div`
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
`;

const ApprovalDetailsSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  padding-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const ApprovalDetailsContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

const IdBadgeWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

const AssigneeSection = styled.div`
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const StagesContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ActivitySection = styled.div`
  flex: 2;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  box-sizing: border-box;

  @media (max-width: 640px) {
    flex: none;
    min-height: auto;
    overflow: visible;
  }
`;

const SectionTitleWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  flex-shrink: 0;
`;

const ActivityLogWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;
  margin-top: ${({ theme }) => theme.resolve("spacing_xl")};
  overflow: hidden;

  @media (max-width: 640px) {
    flex: none;
    min-height: auto;
    overflow: visible;
  }
`;

const EditIconWrapper = styled.div<{ disabled?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  :hover,
  :active {
    border-radius: ${({ theme }) =>
      theme.resolve("radius_buttonIcon_standard")};
    border: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.resolve("color_buttonIcon_border_hover")};
    background: ${({ theme }) => theme.resolve("color_buttonIcon_bg_hover")};
  }
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}
`;

export function ApprovalDetailPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { adminPath } = useRoutePath();
  const { tenant_id, storefront_id, storefront_metadata } = useStoreState();
  const { isXlScreen } = useMediaQueries();
  const {
    user,
    roleIsAdmin,
    roleIsAgilisAdmin,
    hasPermission,
    isLoadingSession,
  } = useAuthContext();
  const { product_id, approval_id } =
    useParams<{
      product_id: string;
      approval_id: string;
    }>();
  const pageLayout =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.product_detail_layout?.value || "single_column";
  const hasEditOrPublishPermission =
    hasPermission("modify_products") || hasPermission("publish_products");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [showApproverModal, setShowApproverModal] = useState(false);
  const { notifySuccess, notifyError } = useContext(Notifications);
  const {
    approval,
    stageValidation,
    attributes,
    isLoading,
    error,
    mutate: mutateApproval,
  } = useApprovalDetail(approval_id ?? null);
  const { updateAssignee } = useApprovalMutations();

  const { data: product } = useSWR<PIMProduct, AxiosError>(
    product_id && !isLoadingSession
      ? makeUrlWithParams(
          endpoints.v2_tenants_id_pim_products_id(tenant_id, product_id),
          {
            use_database: "true",
            return_staged: hasEditOrPublishPermission ? "true" : "false",
          }
        )
      : null,
    { revalidateOnFocus: false }
  );

  const approverSections = useMemo(() => {
    if (!approval) {
      return [];
    }

    return approval.stages.map((stage, index) => {
      const title = stage.display_name || stage.name || "";
      const extraFields = getApproverExtraFields(
        stage.approvers,
        STAGE_APPROVER_IDENTITY_FIELDS
      );

      return {
        key: stage.slug || getApproverSectionKey(stage.name, index),
        title,
        columns: [
          createApproverColumn(
            "approver_name",
            getApproverPersonColumnTitle(title, t),
            "user"
          ),
          ...extraFields.map((field) =>
            createApproverColumn(
              field,
              t(
                attributes?.[field]?.display_name ??
                  toTitleCase(field.replace(/_/g, " "))
              ),
              "single_select",
              attributes?.[field]?.list_id
            )
          ),
        ],
        data: stage.approvers.map((approver) => {
          const row: ApproverRow = {
            id: approver.approver_id,
            approver_name: approver.approver_id,
            approver_name_name: approver.approver_name,
          };
          for (const field of extraFields) {
            row[field] = formatApproverAttributeValue(approver[field]);
          }
          return row;
        }),
        isCompleted: !["pending", "in_progress"].includes(
          stage.stage_status ?? ""
        ),
      };
    });
  }, [approval, attributes, t]);

  const currentStage = approval
    ? findActiveApprovalStage(approval.stages, approval.current_stage)
    : undefined;
  const currentStageIndex = approval
    ? approval.stages.findIndex(
        (stage) =>
          stage === currentStage ||
          stage.slug === approval.current_stage ||
          stage.name === approval.current_stage
      )
    : -1;

  const assigneeFromApi = currentStage?.assignee ?? null;
  const selectedAssignee: AssigneeOption | null = assigneeFromApi
    ? {
        id: String(assigneeFromApi.id),
        name: assigneeFromApi.name,
        email: assigneeFromApi.email,
      }
    : null;
  const [optimisticAssignee, setOptimisticAssignee] =
    useState<AssigneeOption | null | undefined>(undefined);

  useEffect(() => {
    setOptimisticAssignee(undefined);
  }, [assigneeFromApi?.id, assigneeFromApi?.name]);

  const displayAssignee =
    optimisticAssignee !== undefined ? optimisticAssignee : selectedAssignee;

  const assigneeOptions = useMemo(
    () => toAssigneeOptions(currentStage?.approvers ?? []),
    [currentStage?.approvers]
  );

  const canEditAssignee = canEditApprovalAssignee(approval, {
    userId: user?.id,
    roleIsAdmin,
    roleIsAgilisAdmin,
  });

  const assigneeDisabledTooltip = approvalAssigneeDisabledTooltip(t, {
    approval,
    canEdit: canEditAssignee,
    assigneeName: displayAssignee?.name,
  });

  if (isLoading) {
    return (
      <PageRoot>
        <PageContainer>
          <DelayedSpinner />
        </PageContainer>
      </PageRoot>
    );
  }

  if (error || !approval) {
    return (
      <PageRoot>
        <PageContainer>
          <ErrorPlaceholder message={t("Failed to load approval details")} />
        </PageContainer>
      </PageRoot>
    );
  }

  // Check if current user can approve/reject current stage
  const canTakeAction =
    currentStage?.stage_status === "in_progress" &&
    currentStage.approvers.some(
      (a) => a.approver_id === user?.id && a.approver_status === "pending"
    );

  const handleDecisionComplete = async () => {
    await mutateApproval();
    setActivityRefreshKey((prev) => prev + 1);
  };

  const handleAssigneeSelect = async (option: AssigneeOption | null) => {
    setOptimisticAssignee(option);
    try {
      await updateAssignee(approval.id, {
        assignee_id: option?.id ?? null,
      });
      void mutateApproval().then(() => {
        setActivityRefreshKey((prev) => prev + 1);
      });
    } catch {
      setOptimisticAssignee(undefined);
      // Error toast handled in useApprovalMutations
    }
  };

  const onApproverSubmit = async (approvers: ApprovalStage[]) => {
    try {
      await axios.patch(
        endpoints.v2_storefronts_id_integrations_products_id_approvers(
          storefront_id,
          product_id
        ),
        { approval_id: approval.id, stage_approvers: approvers }
      );
      setShowApproverModal(false);
      await mutateApproval();
      setActivityRefreshKey((prev) => prev + 1);
      notifySuccess(t("Approvers updated successfully"));
    } catch (error) {
      console.error("Failed to update approvers:", error);
      notifyError(t("Failed to update approvers"));
    }
  };

  return (
    <PageRoot>
      <Breadcrumb
        pageLayout={pageLayout}
        isXlScreen={isXlScreen}
        style={{ marginBottom: theme.resolve("spacing_xl"), flexShrink: 0 }}
      >
        <LinkUnstyled
          to={`${adminPath}/pim/products${
            sessionStorage.getItem("products_search") ?? ""
          }`}
        >
          {t("Products")}
        </LinkUnstyled>
        <div>/</div>
        <div>{product?.name ?? product_id}</div>
      </Breadcrumb>

      <PageContainer>
        <ContentContainer>
          <StageSection>
            <StageSectionHeader>
              <BackLink>
                <Link
                  to={`${adminPath}/pim/products/${product_id}?tabKey=approvals`}
                  onClick={() => {
                    try {
                      sessionStorage.setItem(APPROVALS_LIST_RESTORE_FLAG, "1");
                    } catch {
                      // Storage unavailable — list just opens fresh.
                    }
                  }}
                >
                  <LinkButtonLargeV2>
                    <CaretLeft fill="currentColor" />
                    {t("Approvals")}
                  </LinkButtonLargeV2>
                </Link>
              </BackLink>
              <ApprovalDetailsSection>
                <ApprovalDetailsContent>
                  <IdBadgeWrapper>
                    <IDMobile>{approval.number}</IDMobile>
                  </IdBadgeWrapper>
                  <TextXlSemibold>{approval.approval_type}</TextXlSemibold>
                  <TextSmRegular color="secondary">
                    {t(attributes?.sku_id?.display_name ?? "ionicPIM SKU ID")}:{" "}
                    {approval.id_value}
                  </TextSmRegular>
                </ApprovalDetailsContent>
                {(roleIsAdmin || roleIsAgilisAdmin) && (
                  <EditIconWrapper
                    disabled={
                      !["pending", "in_progress"].includes(approval.status)
                    }
                    onClick={() =>
                      ["pending", "in_progress"].includes(approval.status) &&
                      setShowApproverModal(true)
                    }
                  >
                    <EditIcon
                      fill={
                        !["pending", "in_progress"].includes(approval.status)
                          ? theme.disabledLinkColor
                          : theme.primaryIconColor
                      }
                    />
                  </EditIconWrapper>
                )}
              </ApprovalDetailsSection>
            </StageSectionHeader>

            {currentStage && (
              <AssigneeSection>
                <TextSmMedium color="tertiary">{t("Assignee")}</TextSmMedium>
                <AssigneePicker
                  name={displayAssignee?.name}
                  selectedId={displayAssignee?.id}
                  stageName={
                    currentStage.display_name || currentStage.name || ""
                  }
                  options={assigneeOptions}
                  disabled={!canEditAssignee}
                  disabledTooltip={assigneeDisabledTooltip}
                  unassignedLabel={t("Unassigned")}
                  onSelect={handleAssigneeSelect}
                  popoverProps={{
                    title: t("Assignee"),
                    searchPlaceholder: t("Search"),
                    stageLabelSuffix: t("review"),
                  }}
                />
              </AssigneeSection>
            )}

            <StagesContainer>
              <ApprovalStageVisualization
                stages={approval.stages}
                currentStage={currentStage?.slug || approval.current_stage}
                canTakeAction={canTakeAction}
                onApprove={() => setShowApproveModal(true)}
                onReject={() => setShowRejectModal(true)}
                stageValidation={stageValidation}
                attributes={attributes}
              />
            </StagesContainer>
          </StageSection>

          <ActivitySection>
            <SectionTitleWrapper>
              <TextXlSemibold>{t("Log")}</TextXlSemibold>
            </SectionTitleWrapper>
            <ActivityLogWrapper>
              <ApprovalActivityLog
                approvalId={approval.id}
                refreshKey={activityRefreshKey}
              />
            </ActivityLogWrapper>
          </ActivitySection>
        </ContentContainer>

        {showApproveModal && currentStage && (
          <ApproveConfirmationModal
            approvalId={approval.id}
            stageName={currentStage.slug || currentStage.name}
            stageDisplayName={currentStage.display_name}
            isLastStage={currentStageIndex === approval.stages.length - 1}
            onClose={() => setShowApproveModal(false)}
            onConfirm={async () => {
              setShowApproveModal(false);
              await handleDecisionComplete();
            }}
          />
        )}

        {showApproverModal && (
          <Approvers
            sections={approverSections}
            onClose={() => {
              setShowApproverModal(false);
              setActivityRefreshKey((prev) => prev + 1);
            }}
            onSubmit={onApproverSubmit}
          />
        )}

        {showRejectModal && currentStage && (
          <RejectConfirmationModal
            approvalId={approval.id}
            stageName={currentStage.slug || currentStage.name}
            stageDisplayName={currentStage.display_name}
            onClose={() => setShowRejectModal(false)}
            onConfirm={async () => {
              setShowRejectModal(false);
              await handleDecisionComplete();
            }}
          />
        )}
      </PageContainer>
    </PageRoot>
  );
}
